
import type { PropType } from 'vue'
import type { FtlOrderSendRequestForPaymentPayload } from '@/core/types/ftl_bids.types'

import { defineComponent, watch, reactive, computed } from 'vue'

import RadioOption from '@/modern/modules/m-dispatcher-module/submodules/waybills/components/waybill-request-for-payment-modal/RadioOption.vue'
import Loader from '@/common/components/UI/Loader/index.vue'

import formatPriceToTenge from '@/modern/helpers/formatPriceToTenge'

import PHONE_MASK from '@/modern/masks/phone.mask'
import CARD_NUMBER_MASK from '@/modern/masks/card-number.mask'
import PRICE_MASK from '@/modern/masks/price.mask'

// > legacy/validation <
import useVuelidate from '@vuelidate/core'
import { required, requiredIf } from '@vuelidate/validators'

// > legacy/notifications <
import useNotifications from '@/common/composable/useNotifications'

import type { FullFTLOrderT } from '@/core/types/orders.types'
import { orders } from '@/core/realisations/orders.realisation'

import { ftl_bids } from '@/core/realisations/ftl_bids.realisation'

export default defineComponent({
    components: { Loader, RadioOption },
    props: {
        disabled: {
            type: Boolean,
            default: false,
        },
        modelValue: {
            type: Boolean,
            default: false,
        },
        ftlOrderId: {
            type: Number as PropType<number | null>,
            default: null
        }
    },
    emits: ['update:modelValue', 'close', 'sended'],
    setup(props, { emit }) {
        const notifications = useNotifications()

        const state = reactive({
            isLoading: false,
            ftlOrder: null as FullFTLOrderT | null,
            paymentSourceId: 10,
            paymentMethodId: 1,
            transferTypeId: 1,
            totalAmount: null as string | null,
            receiverFullName: null as string | null,
            phoneNumber: null as string | null,
            cardNumber: null as string | null,
        })

        const state_form$ = useVuelidate({
            receiverFullName: { required },
            totalAmount: { required: requiredIf(() => state.paymentSourceId === 13) },
            phoneNumber: { required: requiredIf(() => state.transferTypeId === 1) },
            cardNumber: { required: requiredIf(() => state.transferTypeId === 2) },
        }, state)

        const onUpdateModelValue = (newValue: boolean) => {
            if (props.disabled) return
            emit('update:modelValue', newValue)
        }

        const onSelectPaymentSource = (paymentSourceId: number) => {
            state.paymentSourceId = paymentSourceId

            if (paymentSourceId !== 13) {
                state.totalAmount = null
            }
        }

        const onSelectTransferTypeId = (transferTypeId: number) => {
            state.transferTypeId = transferTypeId

            if (transferTypeId === 1) {
                state.cardNumber = null
            } else if (transferTypeId === 2) {
                state.phoneNumber = null
            }
        }

        const calculateFtlOrderAdditionalServicesCost = () => {
            if (!state.ftlOrder) return 0

            if (state.ftlOrder.shared_additional_service.length > 0) {
                const total = state.ftlOrder.shared_additional_service.reduce((sum, service) => sum + Number(service.cost_total || 0), 0)
                return total
            }

            return 0
        }

        const calculateFtlOrderTotalCost = () => {
            if (!state.ftlOrder) return 0

            const servicesCost = calculateFtlOrderAdditionalServicesCost()
            return servicesCost + state.ftlOrder.cost_total
        }

        const loadFtlOrder = async () => {
            if (!props.ftlOrderId) return

            try {
                state.isLoading = true

                const response = await orders.getFTLOrder(props.ftlOrderId)
                state.ftlOrder = response.data
            } catch (error) {
                console.error(error)
            } finally {
                state.isLoading = false
            }
        }

        const onSubmit = async () => {
            if (!state.ftlOrder || !props.ftlOrderId) return

            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            try {
                state.isLoading = true

                let totalAmount = 0

                if (state.paymentSourceId === 10) {
                    totalAmount = Number(state.ftlOrder.cost_total)
                } else if (state.paymentSourceId === 11) {
                    totalAmount = calculateFtlOrderAdditionalServicesCost()
                } else if (state.paymentSourceId === 12) {
                    totalAmount = calculateFtlOrderTotalCost()
                } else if (state.paymentSourceId === 13) {
                    totalAmount = Number((state.totalAmount as string).replace(/\D/g, ""))
                } 

                let number = ''

                if (state.transferTypeId === 1) {
                    number = state.phoneNumber as string
                } else if (state.transferTypeId === 2) {
                    number = state.cardNumber as string
                }

                const payload: FtlOrderSendRequestForPaymentPayload = {
                    paymentSourceId: state.paymentSourceId,
                    paymentMethodId: state.paymentMethodId,
                    transferTypeId: state.transferTypeId,
                    totalAmount,
                    receiverFullName: state.receiverFullName as string,
                    number
                }

                await ftl_bids.sendRequestForPayment(props.ftlOrderId, payload)
                notifications.notify({ type: 'success', title: 'Запрос на выплату отправлен', description: `Запрос на выплату отправлен` })

                onUpdateModelValue(false)
                emit('sended')
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При отправке запроса на выплату произошла ошибка' })
            } finally {
                state.isLoading = false
            }
        }

        watch(
            () => props.modelValue,
            () => {
                state.ftlOrder = null
                state.isLoading = false
                state.paymentSourceId = 10
                state.paymentMethodId = 1
                state.transferTypeId = 1
                state.receiverFullName = null
                state.totalAmount = null
                state.phoneNumber = null
                state.cardNumber = null
                state_form$.value.$reset()

                if (props.modelValue) {
                    loadFtlOrder()
                }
            }
        )

        const totalValue = computed(() => {
            if (state.ftlOrder) {
                if (state.paymentSourceId === 10) {
                    return Number(state.ftlOrder.cost_total)
                }

                if (state.paymentSourceId === 11) {
                    return calculateFtlOrderAdditionalServicesCost()
                }

                if (state.paymentSourceId === 12) {
                    return calculateFtlOrderTotalCost()
                }

                if (state.paymentSourceId === 13) {
                    return state.totalAmount
                }
            }

            return 0
        })

        return {
            state,
            state_form$,
            onUpdateModelValue,
            onSelectPaymentSource,
            onSelectTransferTypeId,
            formatPriceToTenge,
            calculateFtlOrderAdditionalServicesCost,
            calculateFtlOrderTotalCost,
            totalValue,
            PRICE_MASK,
            PHONE_MASK,
            CARD_NUMBER_MASK,
            onSubmit,
        }
    }
})
